import Image from "@/components/common/Image";

type BrandfolderImage = {
  cdn_url?: string;
  url?: string;
  width?: number;
  height?: number;
  filename?: string;
  description?: string;
};

export interface InlineImageProps {
  image?: BrandfolderImage | BrandfolderImage[] | null;
  altText?: string | null;
  size?: "Small" | "Medium" | "Full width" | null;
  alignment?: "Left" | "Center" | "Right" | null;
  caption?: string | null;
  link?: string | null;
}

// Max rendered width per size; images are never upscaled past natural width.
const SIZE_WIDTH = {
  Small: 320,
  Medium: 640,
  "Full width": undefined,
} as const;

// .rich-text is a flex column, so alignment positions the image within the
// text column (no text wrap).
const ALIGNMENT_CLASS = {
  Left: "self-start",
  Center: "self-center",
  Right: "self-end",
} as const;

/**
 * Inline Image — Contentful `inlineImage` entry embedded in rich text
 * (Blog Post body, Text Block). Brandfolder image with size, alignment,
 * optional caption and link.
 */
export default function InlineImage({
  image,
  altText,
  size,
  alignment,
  caption,
  link,
}: InlineImageProps) {
  const img = Array.isArray(image) ? image[0] : image;
  const src = img?.cdn_url || img?.url;
  if (!src) return null;

  const sizeKey = size || "Medium";
  const maxWidth = SIZE_WIDTH[sizeKey];
  const alignKey = alignment || "Center";
  // Editor-entered URL: only allow http(s), mailto, tel, and site-relative links.
  const safeLink =
    link && /^(https?:|mailto:|tel:|\/(?!\/))/i.test(link.trim())
      ? link.trim()
      : undefined;
  const naturalWidth = img?.width;
  const width =
    maxWidth && naturalWidth ? Math.min(maxWidth, naturalWidth) : maxWidth;

  // Brandfolder's CDN resizes with `width=` (it ignores `w=`); request 2x for retina.
  const requestWidth = Math.min((width || 1600) * 2, naturalWidth || Infinity);
  const sizedSrc = `${src}${src.includes("?") ? "&" : "?"}width=${requestWidth}`;

  const picture = (
    <Image
      src={sizedSrc}
      alt={altText || img?.description || ""}
      cover={false}
      imgClassName="w-full h-auto"
    />
  );

  return (
    <figure
      className={`inline-image w-full ${ALIGNMENT_CLASS[alignKey]}`}
      style={width ? { maxWidth: `${width}px` } : undefined}
    >
      {safeLink ? (
        <a
          href={safeLink}
          target={/^https?:\/\//i.test(safeLink) ? "_blank" : undefined}
          rel="noopener noreferrer"
        >
          {picture}
        </a>
      ) : (
        picture
      )}
      {caption && (
        <figcaption className="mt-2 text-sm text-dark-gray">{caption}</figcaption>
      )}
    </figure>
  );
}
